import { Link } from "react-router-dom";
import { ArrowRight, Mic2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { CartoonDollar } from "@/components/CartoonDollar";
import { useSettings } from "@/context/SettingsContext";

const Card = ({ to, testId, accent, children }) => (
  <Link to={to} className={`on-dark group relative flex flex-col overflow-hidden rounded-3xl border grain transition-transform duration-300 hover:-translate-y-1 ${accent}`} data-testid={testId}>
    {children}
  </Link>
);

export const JoinCTA = () => {
  const { homepage } = useSettings();
  const prize = homepage.survey_prize || "$100";
  return (
  <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-14" data-testid="join-cta-section">
    <div className="grid md:grid-cols-2 gap-6">
      <Card to="/join" testId="join-cta-button" accent="border-sky-400/20 bg-[#053671] hover:border-sky-400/50">
        <div className="absolute -top-32 -right-20 h-72 w-72 rounded-full bg-sky-400/25 blur-3xl pointer-events-none transition-transform duration-500 group-hover:scale-125" />
        <Logo size={220} className="absolute -right-12 -bottom-16 opacity-[0.08] rotate-12 pointer-events-none hidden md:block !ring-0 !shadow-none" />
        <div className="relative flex flex-1 flex-col p-8 sm:p-10">
          <p className="eyebrow mb-3 !text-sky-200">{homepage.cta_eyebrow ?? "Podcasters wanted"}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-[1.05]" data-testid="join-cta-title">{homepage.cta_title || "Join Underdog Podcasts"}</h2>
          <p className="mt-4 text-slate-200 leading-relaxed" data-testid="join-cta-text">
            {homepage.cta_text ?? "Bring your podcast to a network built for independent voices. Keep your show, your feed and your voice — we'll add distribution across 50+ shows, cross-promotion on YouTube and social, and a community of hosts who have your back."}
          </p>
          <span className="mt-8 inline-flex items-center gap-3 self-start rounded-full bg-white text-[#053671] px-6 py-3.5 font-display font-extrabold text-base shadow-xl shadow-black/30 transition-colors duration-200 group-hover:bg-sky-300 whitespace-nowrap" data-testid="join-cta-pill">
            <Mic2 size={20} /> {homepage.cta_button || "Pitch your show"} <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </span>
        </div>
      </Card>

      {homepage.survey_enabled !== false && (
      <Card to="/survey" testId="survey-cta-button" accent="border-rose-400/25 bg-[#3B0E2E] hover:border-rose-400/60">
        <div className="absolute -top-28 -left-16 h-72 w-72 rounded-full bg-rose-500/30 blur-3xl pointer-events-none transition-transform duration-500 group-hover:scale-125" />
        <div className="absolute -bottom-24 -right-10 h-60 w-60 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
        <div className="absolute right-4 bottom-2 hidden md:flex items-end pointer-events-none select-none opacity-[0.22] transition-all duration-500 group-hover:opacity-40 group-hover:scale-105" data-testid="survey-cta-dollars">
          <CartoonDollar id="cd1" size={62} className="-rotate-[18deg] translate-y-3" />
          <CartoonDollar id="cd2" size={96} className="-mx-3 -rotate-6 -translate-y-4" />
          <CartoonDollar id="cd3" size={72} className="rotate-[14deg] translate-y-2" />
        </div>
        <div className="relative flex flex-1 flex-col p-8 sm:p-10">
          <p className="eyebrow mb-3 !text-rose-200">{homepage.survey_card_eyebrow ?? `Listeners · win ${prize}`}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-[1.05]" data-testid="survey-cta-title">{homepage.survey_card_title || "Take our listener survey"}</h2>
          <p className="mt-4 text-slate-200 leading-relaxed" data-testid="survey-cta-text">
            {homepage.survey_card_text ?? `Tell us what you love, what you'd change and what you want more of. It takes about five minutes, and every completed survey is entered to win ${prize}.`}
          </p>
          <span className="mt-8 inline-flex items-center gap-3 self-start rounded-full bg-white text-[#3B0E2E] px-6 py-3.5 font-display font-extrabold text-base shadow-xl shadow-black/30 transition-colors duration-200 group-hover:bg-rose-200 whitespace-nowrap" data-testid="survey-cta-pill">
            <CartoonDollar id="cd-btn" size={18} solid="#3B0E2E" /> {homepage.survey_card_button || "Take the survey"} <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </span>
        </div>
      </Card>
      )}
    </div>
  </section>
  );
};
